// Layer 3 (Transformation) — Pending from Advisory on the Maintenance panel row
// (polish.P21, comp A adjudication 2). Pure: no React, no engine mutation.
//
// What Advisory has handed to Maintenance, led by the bearing action: the case
// with a stop plan whose Action stage the cursor has reached (`case-bm-brg`),
// read from the same seeds its case page reads — the stop window, the planned
// and avoided-failure hours, the 85 °C projection from the shared bearing
// trend, the handler, the respond-by clock, the TARP tier of the case's own
// analysis run and who decides the stop. The week-urgency proposed actions on
// maintenance-assigned cases follow (`pendingAdvisoryPmActions`).
//
// No money appears on this route: the money pair stays on the case (adjudication 2).
//
// durable: the Maintenance handoff list over the case seed and recommended actions (ADR-0012).

import type { Case, RecommendedAction } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { BM01_BEARING_STOP_HOURS } from '@/versions/v1/shared/bunker-hill-bearing-stop';
import { bm01BearingTrend } from '@/versions/v1/modules/downtime/ValueLost.transform';
import { plantRoleLabel } from '@/versions/v1/modules/advisory/AgentInvestigation.transform';
import { pendingAdvisoryPmActions } from './Scene.transform';

export type PendingLeadFact =
  | { kind: 'text'; label: string; value: string; sub?: string }
  | { kind: 'handler'; label: string; person: string; since: string }
  | { kind: 'respond'; label: string; at: string; tail: string }
  | { kind: 'stop'; label: string; decides: string; confirms: string };

export type PendingLead = {
  caseId: string;
  code: string;
  /** The stage the case stands at on the cursor. */
  stage: string;
  /** `Plan the 4 h inspection stop on Sat 13 Jun`. */
  proposition: string;
  facts: readonly PendingLeadFact[];
  caseTitle: string;
  /** When the lead needs its answer (the respond-by clock). */
  dueAt: string;
};

export type PendingRow = {
  id: string;
  text: string;
  urgencyLabel: string;
  caseId: string;
};

export type PendingFromAdvisoryRead = {
  lead: PendingLead | null;
  rows: readonly PendingRow[];
  /** `3 actions · 1 due 15:30`. */
  meta: string;
};

function dayClock(at: SeriesTime): string {
  return `${formatDayMonth(at)} ${at.slice(11, 16)}`;
}

function capitalise(value: string): string {
  return value.length === 0 ? value : `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

/** The instant the case reached Action: its handler picked it up on the day it
 *  was raised (`03:20` on `08 Jun`). */
function actionReachedAt(caseItem: Case): SeriesTime | null {
  if (!caseItem.raisedAt || !caseItem.handler) return null;
  return `${caseItem.raisedAt.slice(0, 10)}T${caseItem.handler.since}` as SeriesTime;
}

function leadFor(caseItem: Case, state: SessionState, tarpTier: number | null, approved: boolean): PendingLead | null {
  const plan = caseItem.stopPlan;
  if (!plan) return null;
  const reachedAt = actionReachedAt(caseItem);
  if (reachedAt === null || state.cursor < reachedAt) return null;

  const plannedHours = BM01_BEARING_STOP_HOURS.plannedInspection;
  const avoidedHours = BM01_BEARING_STOP_HOURS.avoidedFailure;
  const trend = bm01BearingTrend(state);
  const projection = trend.state === 'fitted' && trend.limitAt !== null
    ? `vs ${trend.limitC} °C projected ${dayClock(trend.limitAt)}`
    : undefined;
  const facts: PendingLeadFact[] = [
    {
      kind: 'text',
      label: 'Window',
      value: `${plan.window.label} · ${plannedHours} h`,
      sub: projection,
    },
    {
      kind: 'text',
      label: 'One-off',
      value: `${plannedHours} h planned vs ${avoidedHours} h avoided repair`,
      sub: 'priced on the case',
    },
  ];
  if (caseItem.handler) {
    facts.push({
      kind: 'handler',
      label: 'Handler',
      person: `${caseItem.handler.name} · ${plantRoleLabel(caseItem.handler.role) ?? capitalise(caseItem.handler.role)}`,
      since: `since ${caseItem.handler.since}`,
    });
  }
  if (caseItem.respondBy && !approved) {
    const escalate = plantRoleLabel(caseItem.respondBy.escalatesTo) ?? capitalise(caseItem.respondBy.escalatesTo);
    facts.push({
      kind: 'respond',
      label: 'Respond by',
      at: caseItem.respondBy.at,
      tail: [tarpTier !== null ? `TARP tier ${tarpTier}` : null, `then ${escalate}`].filter(Boolean).join(', '),
    });
  }
  if (plan.stopDecision) {
    facts.push({ kind: 'stop', label: 'Stop', decides: plan.stopDecision.decides, confirms: plan.stopDecision.confirms });
  }

  return {
    caseId: caseItem.id,
    code: caseItem.code ?? caseItem.id,
    stage: 'Action',
    proposition: `Plan the ${plannedHours} h inspection stop on ${plan.window.label}`,
    facts,
    caseTitle: caseItem.title,
    dueAt: caseItem.respondBy?.at ?? '',
  };
}

function rowFor(action: RecommendedAction): PendingRow {
  return {
    id: action.id,
    text: action.text,
    urgencyLabel: capitalise(action.urgency),
    caseId: action.caseId,
  };
}

/** The panel's read. `tarpTier` is the case's own analysis run's top TARP
 *  candidate (the tier the case page's gate prints), null until it loads;
 *  `approvedCaseIds` are the cases whose stop Maintenance has approved this
 *  session, whose respond-by clock is then met. */
export function pendingFromAdvisory(input: {
  state: SessionState;
  tarpTier: number | null;
  approvedCaseIds: ReadonlySet<string>;
}): PendingFromAdvisoryRead {
  const { state, tarpTier, approvedCaseIds } = input;
  const caseItem = leadCase();
  const lead = caseItem ? leadFor(caseItem, state, tarpTier, approvedCaseIds.has(caseItem.id)) : null;
  const rows = pendingAdvisoryPmActions().map(rowFor);
  const count = rows.length + (lead ? 1 : 0);
  const due = lead && !approvedCaseIds.has(lead.caseId) && lead.dueAt ? ` · 1 due ${lead.dueAt}` : '';
  return {
    lead,
    rows,
    meta: `${count} action${count === 1 ? '' : 's'}${due}`,
  };
}

function leadCase(): Case | undefined {
  return CASES.find((row) => row.assignedTo === 'maintenance' && row.stopPlan !== undefined);
}

/** The case the lead reads — the maintenance case with a stop plan. */
export function pendingLeadCaseId(): string | undefined {
  return leadCase()?.id;
}

/** The lead case's analysis fixture — the run whose TARP tier the lead reads. */
export function pendingLeadFixture(): string | undefined {
  return leadCase()?.analysisRun?.fixture;
}
